import { useState, useRef, useMemo } from 'react'
import { useLatestCallbackRef } from '~/hooks/useLatestRef'

export function useStateRef<T>(initialState?: T | (() => T)) {
  const stateRef = useRef<T>(
    typeof initialState === 'function'
      ? // @ts-expect-error Known bug: https://github.com/microsoft/TypeScript/issues/37663
        initialState()
      : initialState
  )

  // We use forceUpdate to sometimes trigger a re-render when the state changes
  const [update, forceUpdate] = useState<{ nextValue?: T }>({})

  // when the state changes we want to update the stateRef
  useMemo(() => {
    if (Object.keys(update).length > 0 && update.nextValue !== stateRef.current) {
      // @ts-ignore
      stateRef.current = update.nextValue
    }
  }, [update.nextValue])

  /**
   * A function to update the stateRef value
   *
   * The strategy to use when updating the value
   *   - ref_and_render: updates the ref and triggers a re-render
   *   - next_render: triggers a re-render and waits for the next render to update the ref
   *   - ref_only: only updates the ref and does not trigger a re-render
   */
  const setStateRef = useLatestCallbackRef(function (
    setter: any,
    { strategy = 'ref_and_render' }: { strategy?: 'ref_and_render' | 'next_render' | 'ref_only' } = {}
  ) {
    const newState = typeof setter === 'function' ? setter(stateRef.current) : setter

    if (strategy === 'ref_only') {
      stateRef.current = newState
    } else if (strategy === 'ref_and_render') {
      stateRef.current = newState
      forceUpdate({})
    } else if (strategy === 'next_render') {
      forceUpdate({ nextValue: newState })
    }
  })

  return [stateRef, setStateRef] as const
}
